import type { Look } from './types';

/** 四个分析频段（Hz） */
export const BANDS: { key: 'bandLow' | 'bandLowMid' | 'bandHighMid' | 'bandHigh'; label: string; lo: number; hi: number }[] = [
  { key: 'bandLow', label: '低频', lo: 30, hi: 250 },
  { key: 'bandLowMid', label: '中低', lo: 250, hi: 1000 },
  { key: 'bandHighMid', label: '中高', lo: 1000, hi: 4000 },
  { key: 'bandHigh', label: '高频', lo: 4000, hi: 12000 },
];

/** 每帧输出给渲染器的特征（均为 0..1 左右） */
export interface AudioFeatures {
  /** 原始响度（门限/灵敏度处理后，未平滑） */
  loudRaw: number;
  /** 响度包络（平滑后） */
  loudness: number;
  /** 各频段能量（平滑后） */
  bands: [number, number, number, number];
  /** 按权重合成的频段驱动 */
  bandDrive: number;
  /** 最终驱动量（由模式决定） */
  drive: number;
  /** 起音脉冲（检测到能量骤增时为 1，随后衰减） */
  onset: number;
  /** 是否有真实音频信号在流动 */
  active: boolean;
}

export function zeroFeatures(): AudioFeatures {
  return { loudRaw: 0, loudness: 0, bands: [0, 0, 0, 0], bandDrive: 0, drive: 0, onset: 0, active: false };
}

/**
 * Web Audio 分析引擎
 *   <audio> → MediaElementSource → Analyser → Gain(音量) → destination
 * 分析在音量之前进行，因此调低音量不影响光效强弱。
 */
export class AudioEngine {
  ctx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private gain: GainNode | null = null;
  private timeBuf: Float32Array<ArrayBuffer> = new Float32Array(0);
  private freqBuf: Float32Array<ArrayBuffer> = new Float32Array(0);
  private bandBins: [number, number][] = [];

  readonly features: AudioFeatures = zeroFeatures();
  private agcLoud = { floor: 0.2, peak: 0.6 };
  /** 各频段独立的底噪，共用一个峰值——保留频段之间的相对强弱 */
  private agcBandFloors = [0.2, 0.2, 0.2, 0.2];
  private agcBandPeak = 0.6;
  private slowDrive = 0;
  private volume = 0.8;

  constructor(private readonly el: HTMLAudioElement) {}

  /** 必须在用户手势中调用（浏览器自动播放策略） */
  init(): void {
    if (this.ctx) {
      void this.ctx.resume();
      return;
    }
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctor();
    const src = ctx.createMediaElementSource(this.el);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0; // 平滑由我们自己按起音/释放控制
    analyser.minDecibels = -100;
    analyser.maxDecibels = -10;
    const gain = ctx.createGain();
    gain.gain.value = this.volume;
    src.connect(analyser);
    analyser.connect(gain);
    gain.connect(ctx.destination);
    this.ctx = ctx;
    this.analyser = analyser;
    this.gain = gain;
    this.timeBuf = new Float32Array(analyser.fftSize);
    this.freqBuf = new Float32Array(analyser.frequencyBinCount);
    const binHz = ctx.sampleRate / analyser.fftSize;
    this.bandBins = BANDS.map((b) => [
      Math.max(1, Math.floor(b.lo / binHz)),
      Math.min(analyser.frequencyBinCount - 1, Math.ceil(b.hi / binHz)),
    ]);
    void ctx.resume();
  }

  setVolume(v: number): void {
    this.volume = v;
    if (this.gain && this.ctx) this.gain.gain.setTargetAtTime(v, this.ctx.currentTime, 0.03);
    // 未建立音频图之前，直接用元素音量（iOS 上无效，建立后由 GainNode 接管）
    if (!this.gain) this.el.volume = v;
  }

  /** 每帧调用。dt 单位秒。 */
  update(dt: number, r: Look['reactive']): AudioFeatures {
    const f = this.features;
    const playing = !!this.analyser && !this.el.paused && !this.el.ended && this.el.readyState >= 2;
    let loudIn = 0;
    const bandIn = [0, 0, 0, 0];

    if (playing && this.analyser) {
      this.analyser.getFloatTimeDomainData(this.timeBuf);
      let sum = 0;
      for (let i = 0; i < this.timeBuf.length; i++) sum += this.timeBuf[i] * this.timeBuf[i];
      const rms = Math.sqrt(sum / this.timeBuf.length);
      const db = 20 * Math.log10(rms + 1e-9);
      loudIn = clamp01((db + 60) / 60); // -60 dBFS → 0, 0 dBFS → 1

      this.analyser.getFloatFrequencyData(this.freqBuf);
      for (let b = 0; b < 4; b++) {
        const [lo, hi] = this.bandBins[b];
        let p = 0;
        for (let i = lo; i <= hi; i++) p += Math.pow(10, this.freqBuf[i] / 10);
        const bandDb = 10 * Math.log10(p / (hi - lo + 1) + 1e-12);
        bandIn[b] = clamp01((bandDb + 90) / 70); // -90 dB → 0, -20 dB → 1
      }

      if (r.agc) {
        // 自动增益：跟踪近期底噪与峰值，把两者之间的起伏归一到 0..1，
        // 这样无论录音整体响或轻、是否有持续的底音，光效都有足够起伏。
        loudIn = agc(this.agcLoud, loudIn, dt);
        const maxBand = Math.max(...bandIn);
        this.agcBandPeak = maxBand > this.agcBandPeak ? maxBand : this.agcBandPeak + (maxBand - this.agcBandPeak) * (1 - Math.exp(-dt / 5));
        for (let b = 0; b < 4; b++) {
          const f0 = this.agcBandFloors[b];
          this.agcBandFloors[b] = bandIn[b] < f0 ? bandIn[b] : f0 + (bandIn[b] - f0) * (1 - Math.exp(-dt / 3));
          bandIn[b] = clamp01((bandIn[b] - this.agcBandFloors[b]) / Math.max(0.12, this.agcBandPeak - this.agcBandFloors[b]));
        }
      }
    }

    const shape = (v: number) => {
      const gated = Math.max(0, v - r.threshold) / Math.max(0.0001, 1 - r.threshold);
      return clamp(gated * r.sensitivity, 0, 1.6);
    };
    const kA = 1 - Math.exp(-dt / Math.max(0.001, r.attack / 1000));
    const kR = 1 - Math.exp(-dt / Math.max(0.001, r.release / 1000));
    const follow = (cur: number, target: number) => cur + (target - cur) * (target > cur ? kA : kR);

    f.loudRaw = shape(loudIn);
    f.loudness = follow(f.loudness, f.loudRaw);
    const weights = [r.bandLow, r.bandLowMid, r.bandHighMid, r.bandHigh];
    let wSum = 0;
    let bSum = 0;
    for (let b = 0; b < 4; b++) {
      f.bands[b] = follow(f.bands[b], shape(bandIn[b]));
      wSum += weights[b];
      bSum += weights[b] * f.bands[b];
    }
    f.bandDrive = wSum > 0 ? bSum / wSum : 0;
    f.drive =
      r.mode === 'loudness' ? f.loudness : r.mode === 'bands' ? f.bandDrive : f.loudness * (1 - r.mix) + f.bandDrive * r.mix;

    // 起音检测：驱动量明显高于慢速均值时触发
    this.slowDrive += (f.drive - this.slowDrive) * (1 - Math.exp(-dt / 0.35));
    if (f.drive - this.slowDrive > 0.16 && f.onset < 0.4) f.onset = 1;
    f.onset *= Math.exp(-dt / 0.25);
    f.active = playing;
    return f;
  }
}

interface AgcState {
  floor: number;
  peak: number;
}

/** 峰值：瞬时上升、约 5 秒衰减；底噪：瞬时下降、约 3 秒上升 */
function agc(s: AgcState, v: number, dt: number): number {
  s.peak = v > s.peak ? v : s.peak + (v - s.peak) * (1 - Math.exp(-dt / 5));
  s.floor = v < s.floor ? v : s.floor + (v - s.floor) * (1 - Math.exp(-dt / 3));
  const range = Math.max(0.12, s.peak - s.floor);
  return clamp01((v - s.floor) / range);
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
